import { LOCAL_CONVERSATION_LIMIT } from './constants';

interface LocalConversation {
  id: string;
}

interface LocalConversationStorageKeys {
  conversation: (conversationId: string) => string;
  conversationList: string;
}

export class LocalConversationStorage<TConversation extends LocalConversation> {
  /** In-memory only; persisted conversations are always read from localStorage. */
  private readonly conversationMemory = new Map<string, TConversation>();
  private readonly ephemeralConversationIds = new Set<string>();

  constructor(
    private readonly keys: LocalConversationStorageKeys,
    private readonly limit = LOCAL_CONVERSATION_LIMIT,
  ) {}

  save(conversation: TConversation, options?: { ephemeral?: boolean }): void {
    if (options?.ephemeral) {
      this.ephemeralConversationIds.add(conversation.id);
      this.conversationMemory.set(conversation.id, conversation);
      return;
    }

    this.ephemeralConversationIds.delete(conversation.id);
    this.conversationMemory.delete(conversation.id);

    try {
      localStorage.setItem(
        this.keys.conversation(conversation.id),
        JSON.stringify(conversation),
      );

      const conversationList = this.getAll();
      const existingIndex = conversationList.findIndex(
        item => item.id === conversation.id,
      );

      if (existingIndex >= 0) {
        conversationList.splice(existingIndex, 1);
      }
      conversationList.unshift(conversation);

      const trimmedList = conversationList.slice(0, this.limit);
      const retainedIds = new Set(trimmedList.map(item => item.id));

      for (const item of conversationList) {
        if (!retainedIds.has(item.id)) {
          this.conversationMemory.delete(item.id);
          localStorage.removeItem(this.keys.conversation(item.id));
        }
      }

      localStorage.setItem(
        this.keys.conversationList,
        JSON.stringify(trimmedList),
      );
    } catch {
      // Ignore localStorage failures so chat can continue.
    }
  }

  get(conversationId: string): TConversation | null {
    if (this.ephemeralConversationIds.has(conversationId)) {
      return this.conversationMemory.get(conversationId) ?? null;
    }

    try {
      const stored = localStorage.getItem(
        this.keys.conversation(conversationId),
      );
      if (!stored) {
        this.conversationMemory.delete(conversationId);
        return null;
      }
      return JSON.parse(stored) as TConversation;
    } catch {
      return null;
    }
  }

  getAll(): TConversation[] {
    try {
      const stored = localStorage.getItem(this.keys.conversationList);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  delete(conversationId: string): void {
    this.ephemeralConversationIds.delete(conversationId);
    this.conversationMemory.delete(conversationId);

    try {
      localStorage.removeItem(this.keys.conversation(conversationId));
      const filtered = this.getAll().filter(item => item.id !== conversationId);
      localStorage.setItem(
        this.keys.conversationList,
        JSON.stringify(filtered),
      );
    } catch {
      // Ignore localStorage failures so chat can continue.
    }
  }
}
